import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface ImageSpec {
  url: string;
  alt: string;
}

export interface VideoSpec {
  type: 'youtube' | 'mp4';
  src: string;
  title?: string | null;
}

export interface PageRequest {
  title?: string | null;
  text_blocks?: string[];
  images?: ImageSpec[];
  slider_images?: ImageSpec[];
  video?: VideoSpec | null;
  tailwind_cdn?: boolean;
}

export interface PromptRequest {
  prompt: string;
  tailwind_cdn?: boolean;
}

export interface ElementSelection {
  tagName?: string;
  id?: string | null;
  classes?: string[];
  textContent?: string;
  xpath?: string;
}

export interface PromptModifyHtmlRequest {
  prompt: string;
  html: string;
  context?: {
    selection?: ElementSelection | null;
  };
}

export interface PageResponse {
  html: string;
}

export interface EmailWriterGenerateRequest {
  prompt: string;
  tone?: string;
  audience?: string;
  purpose?: string;
  language?: string;
  subject_hint?: string;
  signature?: string;
  variables?: string[];
}

export interface EmailWriterGenerateResponse {
  subject?: string;
  subject_text?: string;
  generated_subject?: string;
  subject_lines?: string[];
  preheader?: string;
  body?: string;
  body_text?: string;
  generated_body?: string;
  body_html?: string | null;
  html?: string;
  content?: string;
  cta_text?: string;
  cta_link?: string | null;
}

@Injectable({ providedIn: 'root' })
export class AiService {
  private http = inject(HttpClient);
  private readonly agentBase = `${environment.aiBaseUrl}`;

  /** Health check on AI server */
  health(): Observable<{ status: string }> {
    const root = this.agentBase.replace(/\/agent$/, '');
    return this.http.get<{ status: string }>(`${root}/health`);
  }

  /** Render HTML directly from structured request */
  renderHtml(req: PageRequest): Observable<string> {
    return this.http.post(`${this.agentBase}/render`, req, { responseType: 'text' });
  }

  /** Render and return JSON envelope { html } */
  renderJson(req: PageRequest): Observable<PageResponse> {
    return this.http.post<PageResponse>(`${this.agentBase}/render/json`, req);
  }

  /** Get sample generated page (HTML) */
  sampleHtml(): Observable<string> {
    return this.http.get(`${this.agentBase}/sample`, { responseType: 'text' });
  }

  /** Plan via prompt then render HTML (LLM-backed) */
  planRenderHtml(req: PromptRequest): Observable<string> {
    return this.http.post(`${this.agentBase}/plan-render`, req, { responseType: 'text' });
  }

  /** Modify existing website HTML via prompt (proxied through SSR to avoid CORS) */
  modifyWebsiteHtml(req: PromptModifyHtmlRequest): Observable<string> {
    return this.http.post(`${this.agentBase}/modify-website`, req, { responseType: 'text' });
  }
  /** Modify existing website HTML via prompt (proxied through SSR to avoid CORS) */
  programGenerator(req: any): Observable<any> {
    return this.http.post<any>(`${this.agentBase}/program/generate`, req);
  }

  textGenerator(req: any): Observable<any> {
    return this.http.post<any>(`${this.agentBase}/text-professionalize`, req);
  }

  emailWriterGenerate(req: EmailWriterGenerateRequest): Observable<EmailWriterGenerateResponse | string> {
    return this.http.post<EmailWriterGenerateResponse | string>(`${this.agentBase}/email-writer/generate`, req);
  }
}
